import {
  Avatar,
  Badge,
  Box,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  AXIS_PRESENTATION,
  AxisKey,
  ProfessionMatch,
  UniversityProgram,
  identityForUniversity,
  logoUrlForUniversity,
  programsForProfession,
} from "@utils/cognitiveProfile";
import { useCognitiveColor } from "../hooks/useCognitiveFillVariant";

interface CognitiveProgramRecommendationsProps {
  matches: ProfessionMatch[];
}

interface ProgramCardProps {
  program: UniversityProgram;
}

const MAX_PROGRAMS_PER_PROFESSION = 2;
const MAX_PROFESSIONS_ON_PAGE = 4;

const titleOrderForRank = (rank: number): 3 | 4 | 5 => {
  if (rank === 1) return 3;
  if (rank === 2) return 4;
  return 5;
};

function ProgramCard({ program }: ProgramCardProps) {
  const identity = identityForUniversity(program.university);
  const logoSrc = logoUrlForUniversity(program.university);
  const colorFor = useCognitiveColor();

  return (
    <Paper
      p="md"
      radius="sm"
      withBorder
      style={{ borderLeft: `4px solid var(--mantine-color-${identity.color}-6)` }}
    >
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Avatar
          size={56}
          radius="md"
          src={logoSrc}
          color={colorFor(identity.color)}
          variant="filled"
          bg={logoSrc ? "white" : undefined}
          styles={{ image: { objectFit: "contain", padding: 6 } }}
        >
          <Text size="sm" fw={700} c="white">
            {identity.initials}
          </Text>
        </Avatar>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} size="md" lh={1.2}>
            {program.university}
          </Text>
          <Text size="sm" c="dimmed">
            {program.program}
          </Text>
          <Text size="sm" fs="italic" c="var(--oculera-text-secondary)">
            {program.reason}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}

export function CognitiveProgramRecommendations({ matches }: CognitiveProgramRecommendationsProps) {
  const { t } = useTranslation();
  const colorFor = useCognitiveColor();
  const matchesWithPrograms = matches
    .map((match) => ({
      match,
      programs: programsForProfession(match.key).slice(0, MAX_PROGRAMS_PER_PROFESSION),
    }))
    .filter((entry) => entry.programs.length > 0)
    .slice(0, MAX_PROFESSIONS_ON_PAGE);

  return (
    <Paper p="md" radius="md" withBorder shadow="md">
      <Stack gap={2} mb="sm">
        <Text size="xs" c="dimmed" tt="uppercase" fw={700} lts={1.2}>
          {t("cognitiveProfile.programs.eyebrow")}
        </Text>
        <Title order={3}>{t("cognitiveProfile.programs.heading")}</Title>
        <Text size="xs" c="dimmed">
          {t("cognitiveProfile.programs.intro")}
        </Text>
      </Stack>

      {matchesWithPrograms.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("cognitiveProfile.programs.empty")}
        </Text>
      ) : (
        <Stack gap={0}>
          {matchesWithPrograms.map(({ match, programs }, idx) => {
            const rank = idx + 1;
            return (
              <Box key={match.key}>
                <Box py="xs">
                  <Group gap="md" align="center" mb="xs" wrap="nowrap">
                    <Text fz={20} fw={300} c="dimmed" lh={1} style={{ minWidth: 28 }}>
                      {rank}
                    </Text>
                    <Title
                      order={titleOrderForRank(rank)}
                      style={{ flex: 1, minWidth: 0 }}
                      lh={1.2}
                      tt="none"
                    >
                      {t(`cognitiveProfile.professions.${match.key}`, {
                        defaultValue: match.key,
                      })}
                    </Title>
                    <Group gap={4} wrap="wrap" justify="flex-end" style={{ flexShrink: 0 }}>
                      {match.axes.map((axisKey: AxisKey) => (
                        <Badge
                          key={axisKey}
                          color={colorFor(AXIS_PRESENTATION[axisKey].color)}
                          variant="filled"
                          size="xs"
                          tt="none"
                        >
                          {t(`cognitiveProfile.axes.${axisKey}`)}
                        </Badge>
                      ))}
                    </Group>
                  </Group>

                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" className="print-cols-2">
                    {programs.map((program, programIdx) => (
                      <ProgramCard key={`${program.university}-${programIdx}`} program={program} />
                    ))}
                  </SimpleGrid>
                </Box>
                {idx < matchesWithPrograms.length - 1 && <Divider />}
              </Box>
            );
          })}
        </Stack>
      )}

      <Text size="xs" c="dimmed" mt="md" fs="italic">
        {t("cognitiveProfile.programs.disclaimer")}
      </Text>
    </Paper>
  );
}
